import { api } from "@/lib/api";
import type {
  Guest,
  CreateGuestDto,
  CreateBulkGuestsDto,
  UpdateGuestDto,
  CheckInGuestDto,
} from "@/types/guest";

class GuestService {
  /**
   * Get all guests (optionally filtered by eventId and include timestamps)
   */
  async getAll(options?: {
    eventId?: string;
    includeTimestamps?: boolean;
  }): Promise<Guest[]> {
    const params = new URLSearchParams();
    if (options?.eventId) params.append("eventId", options.eventId);
    if (options?.includeTimestamps) params.append("include", "timestamps");

    // The backend returns { guests: [], statistics: {} } structure, not just []
    // We type it as any to handle the extraction, then return Guest[]
    const response = await api.get<unknown>(`/guests?${params}`);
    return (response as { guests?: Guest[] }).guests ?? [];
  }

  /**
   * Get a single guest by ID
   */
  async getById(id: string): Promise<Guest> {
    return api.get<Guest>(`/guests/${id}`);
  }

  /**
   * Create a new guest
   */
  async create(data: CreateGuestDto): Promise<Guest> {
    return api.post<Guest>("/guests", data);
  }

  /**
   * Create multiple guests at once
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createBulk(data: CreateBulkGuestsDto): Promise<any> {
    return api.post("/guests/bulk", data);
  }

  /**
   * Update a guest
   */
  async update(id: string, data: UpdateGuestDto): Promise<Guest> {
    return api.patch<Guest>(`/guests/${id}`, data);
  }

  /**
   * Delete a guest
   */
  async delete(id: string): Promise<Guest> {
    return api.delete<Guest>(`/guests/${id}`);
  }

  /**
   * Check in a guest
   */
  async checkIn(
    id: string,
    data?: CheckInGuestDto,
  ): Promise<{
    id: string;
    name: string;
    checkedIn: boolean;
    checkedInFriends: number;
    checkedInAt: string;
    allowedFriends: number;
    totalCheckedIn: number;
    summary: unknown;
  }> {
    return api.post(`/guests/${id}/check-in`, data || {});
  }

  /**
   * Check out a guest
   */
  async checkOut(id: string): Promise<Guest> {
    return api.post<Guest>(`/guests/${id}/check-out`);
  }
}

export const guestService = new GuestService();
